import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CircleCheck, Clock, MessageSquareWarning, TriangleAlert } from 'lucide-react';
import type { Utilisateur } from '@/shared/auth/types';
import { depots } from '@/shared/donnees';
import { aujourdhui } from '@/shared/referentiel/conformite';
import { cn, useDonnees } from '@/shared/ui';
import { notifications, type Notification } from '../notifications';

const ICONE: Record<Notification['ton'], typeof Bell> = {
  danger: TriangleAlert,
  attention: MessageSquareWarning,
  info: Clock,
  succes: CircleCheck,
};

const COULEUR: Record<Notification['ton'], string> = {
  danger: 'bg-danger-bg text-danger',
  attention: 'bg-warning-bg text-warning',
  info: 'bg-info-bg text-info',
  succes: 'bg-success-bg text-success',
};

/**
 * La cloche de l'en-tête : ce qui demande votre attention. Elle se relit d'elle-même
 * quand un chantier ou un ticket change (le direct) ; le minuteur ne sert plus
 * qu'à ce qui change avec l'heure seule (une échéance dépassée) et de filet si le
 * direct est coupé.
 */
export function Cloche({ utilisateur }: { utilisateur: Utilisateur }) {
  const [ouverte, setOuverte] = useState(false);
  const cadre = useRef<HTMLDivElement>(null);
  const { lecture, relire } = useDonnees(async () => {
    try {
      const [chantiers, tickets] = await Promise.all([
        depots().chantiers.lister(),
        depots()
          .sav.lister()
          .catch(() => []),
      ]);
      return notifications(chantiers, utilisateur, aujourdhui(), new Date(), tickets);
    } catch {
      return [];
    }
  }, utilisateur.id);

  useEffect(() => {
    const minuterie = window.setInterval(relire, 600_000);
    return () => {
      window.clearInterval(minuterie);
    };
  }, [relire]);

  useEffect(() => {
    if (!ouverte) return undefined;
    function dehors(evenement: MouseEvent) {
      if (!cadre.current?.contains(evenement.target as Node)) setOuverte(false);
    }
    function echap(evenement: KeyboardEvent) {
      if (evenement.key === 'Escape') setOuverte(false);
    }
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => {
      document.removeEventListener('mousedown', dehors);
      document.removeEventListener('keydown', echap);
    };
  }, [ouverte]);

  const liste = lecture.statut === 'pret' ? lecture.donnees : [];
  const aAgir = liste.filter(
    (n) => n.ton === 'danger' || n.ton === 'attention' || n.ton === 'info'
  ).length;

  return (
    <div ref={cadre} className="relative">
      <button
        type="button"
        onClick={() => {
          setOuverte((o) => !o);
          relire();
        }}
        aria-expanded={ouverte}
        aria-label={aAgir > 0 ? `Notifications, ${aAgir} à traiter` : 'Notifications'}
        className="relative grid size-[38px] place-items-center rounded-[10px] border border-border bg-surface text-muted hover:text-ink"
      >
        <Bell className="size-[18px]" aria-hidden />
        {aAgir > 0 ? (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] rounded-full border-2 border-background bg-danger px-1 text-center text-[10px] leading-[14px] font-semibold text-white">
            {aAgir}
          </span>
        ) : null}
      </button>
      {ouverte ? (
        <div className="absolute right-0 z-40 mt-2 w-[min(92vw,380px)] overflow-hidden rounded-2xl border border-border bg-surface shadow-xl">
          <p className="border-b border-border-subtle px-4 py-3 text-sm font-semibold">
            Notifications
          </p>
          {liste.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-muted">Rien à signaler.</p>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto p-2">
              {liste.map((n) => {
                const Icone = ICONE[n.ton];
                return (
                  <li key={n.id}>
                    <Link
                      to={n.lien}
                      onClick={() => {
                        setOuverte(false);
                      }}
                      className="flex gap-3 rounded-xl px-2.5 py-2 hover:bg-surface-hover"
                    >
                      <span
                        className={cn(
                          'grid size-8 shrink-0 place-items-center rounded-lg',
                          COULEUR[n.ton]
                        )}
                      >
                        <Icone className="size-4" aria-hidden />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] font-medium">{n.titre}</span>
                        <span className="line-clamp-2 block text-xs text-muted">{n.detail}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
